/**
 * Input with Start Select Renderer
 *
 * Text input with a dropdown selector at the start (e.g., URL with protocol)
 */

import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { FieldRenderer } from '@/MyCode/core';

/**
 * Input with Start Select Renderer
 * Features:
 * - Dropdown selector on the left
 * - Combined value (select + input)
 * - Perfect for URLs (https://, http://, etc.)
 * - Auto-parsing of values
 */
export const renderInputWithStartSelect: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    isRequired
}) => {
    // Parse the combined value to separate select and input parts
    const fullValue = value || '';
    const defaultSelectValue = field.ui?.startSelectOptions?.[0]?.value || '';

    // Find which option prefix matches the current value
    let selectValue = defaultSelectValue;
    let inputValue = fullValue;

    if (fullValue && field.ui?.startSelectOptions) {
        for (const option of field.ui.startSelectOptions) {
            if (fullValue.startsWith(option.value)) {
                selectValue = option.value;
                inputValue = fullValue.substring(option.value.length);
                break;
            }
        }
    }

    const handleSelectChange = (newSelectValue: string) => {
        // Combine the new select value with existing input value
        const combinedValue = newSelectValue + inputValue;
        handleChange(field.name, combinedValue);
    };

    const handleInputChange = (newInputValue: string) => {
        // Combine the current select value with new input value
        const combinedValue = selectValue + newInputValue;
        handleChange(field.name, combinedValue);
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div className="flex rounded-md shadow-xs">
                <select
                    value={selectValue}
                    onChange={(e) => handleSelectChange(e.target.value)}
                    className="w-fit rounded-e-none text-muted-foreground shadow-none hover:text-foreground border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                    {field.ui?.startSelectOptions?.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <Input
                    id={field.name}
                    type={field.inputType || 'text'}
                    placeholder={field.placeholder}
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    maxLength={field.maxLength}
                    className={`-ms-px rounded-s-none shadow-none focus-visible:z-10 ${hasError ? 'border-destructive' : ''}`}
                />
            </div>
        </div>
    );
};

export default renderInputWithStartSelect;
